import { useQueryClient } from '@tanstack/react-query';
import { useServerTable } from '@/components/data-grid';
import { selectData, type PageResponse } from '@/api/envelope';
import {
  searchUsers,
  getUserDetail,
  useGetUserDetail,
  getGetUserDetailQueryKey,
  useChangeUserStatus,
} from '@/api/generated/user-manager/user-manager';
import { getGetModerationHistoryByContentQueryKey } from '@/api/generated/moderation/moderation';
import {
  getTopContent,
  getEarningTransactions,
  useGetEngagements,
  useGetEngagementCount,
  useGetTopContent,
  useGetEarnings,
} from '@/api/generated/statistics/statistics';
import type {
  AdminUserDetailsResponseDTO,
  UserMgmtResponseDTO,
  SearchUsersParams,
  PostResponseDTO,
  GetEngagementsParams,
  GetEngagementCountParams,
  GetEarningsParams,
  EarningTransactionRequestDTO,
  EarningTransactionResponseDTO,
} from '@/api/generated/model';

/**
 * Thin data-layer wrappers over the generated user-manager orval hooks (doc 06 §4.2).
 *
 * The real spec types `searchUsers` rows as `UserMgmtResponseDTO` (rich profile row) and takes a
 * nested `{ requestDTO: { key, pageNumber, pageSize } }` param object (SearchUsersParams).
 */

/** List: server-paginated user search wired into the shared useServerTable (doc 05). */
export function useUsersList() {
  return useServerTable<UserMgmtResponseDTO>({
    // queryKey base — search/filters are appended internally by useServerTable.
    queryKey: ['users', 'search'],
    fetchPage: ({ pageParam, pageSize, search, filters }) => {
      const trimmed = search.trim();
      // TM-3211: Login Type filters server-side, so it covers the whole result set rather than
      // whichever pages happen to be loaded. Values are authentication_method codes.
      const loginType = filters?.loginType ?? [];
      // Location filters server-side too: filtering it over loaded rows left the viewport unfilled,
      // which kept re-triggering the scroll sentinel until it had walked the whole users table.
      const location = filters?.location ?? [];
      // Real param shape is a nested `requestDTO`. Omit `key` when blank —
      // exactOptionalPropertyTypes forbids an explicit `undefined`.
      const params: SearchUsersParams = {
        requestDTO: {
          pageNumber: pageParam,
          pageSize,
          ...(trimmed ? { key: trimmed } : {}),
          ...(loginType.length ? { loginType } : {}),
          ...(location.length ? { location } : {}),
        },
      };
      return searchUsers(params).then((res) => {
        const page = selectData<PageResponse<UserMgmtResponseDTO>>(res);
        // Envelope `data` may be null — guard with an empty terminal page (shared contract).
        return page ?? { rows: [], pageNumber: 1, totalPage: 1, totalElements: 0 };
      });
    },
    pageSize: 20, // parity with CMP size=20
    mode: 'paged',
    urlKey: 'users', // keep search/filters/page across list -> detail -> Back
  });
}

/**
 * User detail "All Content" grid (op #6). The statistics endpoint returns a BARE
 * PageResponse<PostResponseDTO> (shape B — no SuccessResponseDTO envelope), so fetchPage returns it
 * as-is. queryKey includes userId so navigating between users resets paging (CMP reset-on-userId).
 */
export function useUserTopContent(userId: string) {
  const id = Number(userId);
  return useServerTable<PostResponseDTO>({
    queryKey: ['users', userId, 'top-content'],
    fetchPage: async ({ pageParam, pageSize }) => {
      const res = await getTopContent(id, { requestDTO: { pageNumber: pageParam, pageSize } });
      return res ?? { rows: [], pageNumber: 1, totalPage: 1, totalElements: 0 };
    },
    pageSize: 12,
    mode: 'paged', // paged, not infinite: this card sits above others on the detail page,
    // and unbounded growth pushed everything below it off-screen.
  });
}

/** Detail: single-user admin detail (op #2). Enveloped (shape A) -> unwrap with selectData. */
export function useUserDetail(userId: string) {
  const id = Number(userId);
  // Explicit TData so select inference cannot collapse to `unknown` (verbatim envelope-select pattern).
  return useGetUserDetail<AdminUserDetailsResponseDTO>(id, {
    query: {
      select: selectData,
      enabled: !!userId && !Number.isNaN(id),
    },
  });
}

/**
 * Change-status mutation (op #3) — used by the admins-only Ban action. On success, invalidate the
 * detail query so the status badge/section refetch, and the user moderation-history query so the
 * new reason row appears without leaving the page. Call: `mutate({ data: { userId, userStatus } })`.
 */
export function useChangeStatus(userId: string) {
  const qc = useQueryClient();
  return useChangeUserStatus({
    mutation: {
      onSuccess: () => {
        const id = Number(userId);
        void qc.invalidateQueries({ queryKey: getGetUserDetailQueryKey(id) });
        // UserModerationHistory reads by content (contentType U), a separate cache entry from
        // user detail. Default staleTime is 30s and refetchOnWindowFocus is off.
        void qc.invalidateQueries({
          queryKey: getGetModerationHistoryByContentQueryKey(id, { contentType: 'U' }),
        });
      },
    },
  });
}

// ─── Engagement statistics (op #9/#10/#6 preview) ────────────────────────────
// The generated hooks below DO exist (src/api/generated/statistics/statistics.ts) — despite
// stale comments elsewhere in this feature previously claiming otherwise. Note the userId typing
// split: engagements/engagement-count take `userId: string`, top-content takes `userId: number`.

/** Engagement time series for the line chart (op #9). Bare `TimeSlicedStatisticsDTO` (shape B). */
export function useEngagementSeries(userId: string, params: GetEngagementsParams) {
  return useGetEngagements(userId, params, { query: { enabled: !!userId } });
}

/** Visits/subscribers counts for the same window (op #10). Bare `{[key]: string}` map (shape B). */
export function useEngagementCounts(userId: string, params: GetEngagementCountParams) {
  return useGetEngagementCount(userId, params, { query: { enabled: !!userId } });
}

/**
 * Small non-paginated Top Content preview for the Engagement section (first page only; distinct
 * from `useUserTopContent`'s infinite-scroll "All Content" grid, which hits the same endpoint).
 * Also the real source for the "Total Contents" metric card (`data.totalElements`).
 */
export function useTopContentPreview(userId: string, pageSize = 8) {
  const id = Number(userId);
  return useGetTopContent(
    id,
    { requestDTO: { pageNumber: 1, pageSize } },
    { query: { enabled: Number.isFinite(id) && id > 0 } },
  );
}

// ─── Earnings overview (op #7/#8) ─────────────────────────────────────────────

/** Categorised earnings over time for the Earnings section (op #7). Bare `TimeSlicedStatisticsDTO`. */
export function useEarningsSeries(userId: string, params: GetEarningsParams) {
  return useGetEarnings(userId, params, { query: { enabled: !!userId } });
}

/**
 * Earning transaction history (op #8), server-paginated via the shared `useServerTable` — parity
 * with `useUserTopContent`'s use of the raw `getTopContent` fetcher (bare PageResponse, shape B).
 * `startDate`/`endDate` are required by the backend DTO, so callers must supply a resolved range.
 */
export function useEarningTransactionsList(
  userId: string,
  startDate: string,
  endDate: string,
  category?: string,
) {
  return useServerTable<EarningTransactionResponseDTO>({
    queryKey: ['users', userId, 'earning-transactions', startDate, endDate, category ?? 'ALL'],
    fetchPage: async ({ pageParam, pageSize }) => {
      const requestDTO: EarningTransactionRequestDTO = category
        ? { startDate, endDate, pageNumber: pageParam, pageSize, category }
        : { startDate, endDate, pageNumber: pageParam, pageSize };
      const res = await getEarningTransactions(userId, { requestDTO });
      return res ?? { rows: [], pageNumber: 1, totalPage: 1, totalElements: 0 };
    },
    pageSize: 10,
    mode: 'paged',
  });
}

// Re-export the fetcher so callers/tests can reference the raw endpoint if needed.
export { getUserDetail };
